// src/pages/admin/AdminRecordingsPage.jsx
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Alert, LinearProgress,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Button, TextField, Select,
  MenuItem, FormControl, InputLabel, Divider, Switch,
  FormControlLabel, Snackbar,
} from '@mui/material';
import VideocamIcon   from '@mui/icons-material/Videocam';
import RefreshIcon    from '@mui/icons-material/Refresh';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SaveIcon       from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { getRecordingConfig, updateRecordingConfig } from '../../api/adminRecordingsApi';
import api from '../../api/axios';

const PROVIDERS = ['local', 's3', 'minio', 'scaleway', 'r2'];

export default function AdminRecordingsPage() {
  const { t }        = useTranslation('admin');
  const [config,     setConfig]     = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [snack,      setSnack]      = useState('');

  const loadConfig = useCallback(async () => {
    try {
      const r = await getRecordingConfig();
      setConfig(r.data);
    } catch { setConfig({ enabled: false, provider: 'local', retention_days: 90 }); }
  }, []);

  const loadRecordings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/recordings', { params: { limit: 50 } });
      setRecordings(r.data?.recordings || r.data || []);
    } catch { setRecordings([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConfig(); loadRecordings(); }, [loadConfig, loadRecordings]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await updateRecordingConfig(config);
      setSnack('Configuration sauvegardee');
    } catch(e) {
      setError(e?.response?.data?.detail?.message || 'Erreur sauvegarde');
    } finally { setSaving(false); }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <VideocamIcon sx={{ fontSize: 28, color: '#4f46e5' }} />
          <Typography variant="h5" fontWeight={700}>{t('recordings')}</Typography>
        </Stack>
        <IconButton onClick={() => { loadConfig(); loadRecordings(); }}><RefreshIcon /></IconButton>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Config */}
      {config && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
          <Typography fontSize={13} fontWeight={600} mb={2}>Configuration</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
            <FormControlLabel
              control={<Switch checked={config.enabled || false}
                onChange={e => setConfig(c => ({ ...c, enabled: e.target.checked }))} />}
              label="Enregistrement actif"
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Provider</InputLabel>
              <Select value={config.provider || 'local'}
                onChange={e => setConfig(c => ({ ...c, provider: e.target.value }))} label="Provider">
                {PROVIDERS.map(p => <MenuItem key={p} value={p}>{p.toUpperCase()}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" label="Retention (jours)" type="number"
              value={config.retention_days || 90} sx={{ width: 150 }}
              onChange={e => setConfig(c => ({ ...c, retention_days: parseInt(e.target.value) }))} />
            <Button variant="contained" disableElevation startIcon={<SaveIcon />}
              onClick={handleSaveConfig} disabled={saving}
              sx={{ bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' }, borderRadius: 2 }}>
              Sauvegarder
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Liste recordings */}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography fontSize={13} fontWeight={600}>Enregistrements recents</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Reservation</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Taille</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recordings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Aucun enregistrement.
                </TableCell>
              </TableRow>
            ) : recordings.map((r, i) => (
              <TableRow key={r.id || i} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{String(r.id).slice(0,8)}…</TableCell>
                <TableCell sx={{ fontSize: 12 }}>#{r.reservation_id}</TableCell>
                <TableCell>
                  <Chip label={r.recording_type || 'platform'} size="small"
                    sx={{ fontSize: 10, bgcolor: '#eef2ff', color: '#4f46e5' }} />
                </TableCell>
                <TableCell>
                  <Chip label={r.status || 'unknown'} size="small"
                    color={r.status === 'finalized' ? 'success' : r.status === 'recording' ? 'warning' : 'default'} />
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.size_mb ? r.size_mb + ' Mo' : '-'}</TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {r.created_at ? new Date(r.created_at).toLocaleString('fr') : '-'}
                </TableCell>
                <TableCell>
                  {r.status === 'finalized' && (
                    <IconButton size="small" component="a"
                      href={'/api/v1/recordings/' + r.id + '/url'} target="_blank">
                      <PlayCircleIcon fontSize="small" sx={{ color: '#4f46e5' }} />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
